import { HighlightBanner, Section } from "@/app/components/brand-scaffold";
import { subcontractBullets } from "@/design-system/registry/homepage";

/**
 * S6 · Subcontract Network — Hotline 之间可以转包
 *
 * Layout:
 *   - Top: brutalist topology SVG (1 inbound Hotline → 3 upstream Hotlines)
 *   - Below: 3 bullets explaining the network effect
 *   - Closing: highlight banner
 */
export function HomepageSubcontract() {
  return (
    <Section
      id="network"
      eyebrow="THE NETWORK EFFECT"
      title="一根 Hotline 可以由别的 Hotline 实现"
      description="你卖出的 Hotline 背后可以串着别人的 Hotline。整张网络自动复合，结算由协议层负责。"
    >
      <div className="border-4 border-black bg-white p-5 sm:p-8 shadow-[10px_10px_0px_0px_rgba(0,0,0,1)]">
        {/* Topology SVG */}
        <div className="bg-zinc-50 border-4 border-black p-4 sm:p-6">
          <svg
            viewBox="0 0 900 460"
            role="img"
            aria-label="Hotline 转包拓扑：你卖出的 Hotline 在顶部，向下分三路调用三根上游 Hotline。"
            className="w-full h-auto block"
            style={{ background: "#FAFAFA", fontFamily: 'Inter, "Helvetica Neue", Helvetica, Arial, sans-serif' }}
          >
            {/* Caller (Agent) eyebrow watermark */}
            <text x="40" y="40" fontSize="12" fontWeight="900" fill="#9CA3AF" letterSpacing="2">
              AGENT 调用 →
            </text>

            {/* Top: 你卖出的 Hotline */}
            <rect x="278" y="78" width="364" height="100" fill="#111111" />
            <rect x="270" y="70" width="364" height="100" fill="#A3E635" stroke="#111111" strokeWidth="6" />
            <text x="452" y="113" fontSize="22" fontWeight="900" fill="#111111" textAnchor="middle" letterSpacing="0.5">
              你对外发布的 HOTLINE
            </text>
            <text x="452" y="142" fontSize="12" fontWeight="900" fill="#111111" textAnchor="middle" letterSpacing="1.5">
              （AGENT 调用的入口）
            </text>

            {/* Splitter — vertical trunk + 3 branches */}
            <path d="M 452 178 L 452 220 L 150 220 L 150 270" fill="none" stroke="#111111" strokeWidth="5" />
            <path d="M 452 220 L 452 270" fill="none" stroke="#111111" strokeWidth="5" />
            <path d="M 452 220 L 754 220 L 754 270" fill="none" stroke="#111111" strokeWidth="5" />

            {/* Arrowheads pointing down to each upstream box */}
            <polygon points="150,285 140,265 160,265" fill="#111111" />
            <polygon points="452,285 442,265 462,265" fill="#111111" />
            <polygon points="754,285 744,265 764,265" fill="#111111" />

            {/* Three upstream Hotlines */}
            {[
              { cx: 150, label: "上游 HOTLINE A", color: "#3B82F6", textColor: "#FFFFFF" },
              { cx: 452, label: "上游 HOTLINE B", color: "#8B5CF6", textColor: "#FFFFFF" },
              { cx: 754, label: "上游 HOTLINE C", color: "#F97316", textColor: "#FFFFFF" },
            ].map((node) => (
              <g key={node.label}>
                <rect x={node.cx - 90} y="298" width="180" height="100" fill="#111111" />
                <rect
                  x={node.cx - 98}
                  y="290"
                  width="180"
                  height="100"
                  fill={node.color}
                  stroke="#111111"
                  strokeWidth="6"
                />
                <text
                  x={node.cx - 8}
                  y="333"
                  fontSize="14"
                  fontWeight="900"
                  fill={node.textColor}
                  textAnchor="middle"
                  letterSpacing="1"
                >
                  {node.label}
                </text>
                <text
                  x={node.cx - 8}
                  y="362"
                  fontSize="11"
                  fontWeight="900"
                  fill={node.textColor}
                  textAnchor="middle"
                  letterSpacing="0.8"
                  fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
                >
                  by another OPC
                </text>
              </g>
            ))}

            {/* Bottom watermark */}
            <text x="452" y="438" fontSize="11" fontWeight="900" fill="#9CA3AF" textAnchor="middle" letterSpacing="2">
              SETTLEMENT · 调用计费由协议层自动结算到每个上游
            </text>
          </svg>
        </div>

        {/* 3 bullet lines */}
        <div className="mt-8 grid gap-4 md:grid-cols-3">
          {subcontractBullets.map((b, idx) => (
            <div
              key={b.title}
              className="border-2 border-black bg-white/90 p-4 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]"
            >
              <span className="inline-flex items-center justify-center h-8 w-8 border-2 border-black bg-[#A3E635] text-sm font-black text-black">
                {String(idx + 1).padStart(2, "0")}
              </span>
              <h3 className="mt-3 text-base font-black text-black leading-tight">
                {b.title}
              </h3>
              <p className="mt-2 text-sm font-bold text-zinc-700 leading-relaxed">
                {b.body}
              </p>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-8">
        <HighlightBanner
          badgeText="NETWORK"
          badgeColor="bg-[#3B82F6]"
          shadowClass="shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]"
        >
          <p className="text-sm sm:text-base font-bold text-zinc-900 leading-relaxed">
            人类公司如何分包，
            <span className="font-black text-black">Hotline 网络就如何分包</span>
            ——只是把"招标 / 议价 / 结款"全换成了协议层自动完成。
          </p>
        </HighlightBanner>
      </div>
    </Section>
  );
}
